import { Injectable, inject, signal, OnDestroy } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Subject } from "rxjs";
import { toObservable } from "@angular/core/rxjs-interop";
import { takeUntil } from "rxjs/operators";
import { v4 as uuidv4 } from "uuid";
import { OAuthService } from "angular-oauth2-oidc";
import { environment } from "../../../environments/environment";
import {
  ConnectionState,
  WebSocketEventService,
} from "./websocket-event.service";
import {
  Message,
  ArticleNotification,
  ProcessMessage,
  TraceType,
} from "../models/message.model";
import { parseMessageContent } from "../utils/message-parser.util";
import { getSecureRandomIndex } from "../utils/secure-random.util";
import { TraceFormatterService } from "./trace-formatter.service";

/**
 * Event message structure from AppSync Event API
 */
interface EventMessage {
  id?: string;
  type: string;
  event?: {
    type: string;
    agentName?: string;
    sessionId?: string;
    messageId?: string;
    timestamp?: string;
    body?: any;
  };
}

/**
 * Chat service for AppSync Event API integration.
 * Handles WebSocket connections and HTTP message sending to Event API endpoints.
 */
@Injectable({
  providedIn: "root",
})
export class ChatEventService implements OnDestroy {
  private readonly webSocketEventService = inject(WebSocketEventService);
  private readonly http = inject(HttpClient);
  private readonly oauthService = inject(OAuthService, { optional: true });
  private readonly traceFormatter = inject(TraceFormatterService);
  private readonly destroy$ = new Subject<void>();
  private isDestroyed = false;

  // AppSync Event API configuration (set at build time)
  private readonly config = environment.chatEvent;

  // State
  private readonly messagesState = signal<Message[]>([]);
  private readonly isTypingState = signal<boolean>(false);
  private readonly connectedState = signal<boolean>(false);
  private readonly botCodeState = signal<string>("");
  private readonly sessionId = signal<string>(this.generateSessionId());
  private lastMessageTimestamp: Date | null = null;
  private readonly connectionState = signal<ConnectionState>("DISCONNECTED");
  private readonly pendingProcessMessagesState = signal<ProcessMessage[]>([]); // Accumulate process messages until assistant responds
  private readonly isThinkingState = signal<boolean>(false); // Indicates if agent is thinking (has pending process messages)
  private retrievedArticles: Record<string, any[]> = {};


  // Expose signals
  public messages = this.messagesState;
  public isTyping = this.isTypingState;
  public connected = this.connectedState;
  public botCode = this.botCodeState;
  public connection = this.connectionState;
  public sessionIdSignal = this.sessionId;
  public isThinking = this.isThinkingState;
  public pendingProcessMessages = this.pendingProcessMessagesState;

  // Convert signals to observables for template async pipe
  public messages$ = toObservable(this.messages);
  public isTyping$ = toObservable(this.isTyping);
  public connected$ = toObservable(this.connected);
  public botCode$ = toObservable(this.botCode);
  public connection$ = toObservable(this.connection);
  public sessionId$ = toObservable(this.sessionIdSignal);

  constructor() {
    this.setupWebSocketHandlers();
  }

  /**
   * Initializes the WebSocket connection
   */
  private initializeWebSocketConnection(botCode: string): void {
    if (this.isDestroyed) return;

    // Set API key and endpoint from config
    this.webSocketEventService.setApiKey(this.config.wsApiKey);
    this.webSocketEventService.setEndpoint(this.config.wsEndpoint);

    this.webSocketEventService.connect(
      this.sessionId(),
      botCode,
    );
  }

  private setupWebSocketHandlers(): void {
    if (this.isDestroyed) {
      return;
    }

    this.setupConnectionStateListener();
    this.setupMessageListener();
  }

  private setupConnectionStateListener(): void {
    this.webSocketEventService.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (state: ConnectionState) => {
          this.connection.set(state);
          this.connectedState.set(state === "SUBSCRIBED" || state === "CONNECTED");
        },
        error: (error) => {
          // Connection errors are handled by the WebSocket service
        },
      });
  }

  private setupMessageListener(): void {
    this.webSocketEventService.messages$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => this.processIncomingMessage(data),
        error: (error) => {
          if (!this.isDestroyed) {
            // Message errors are logged by the WebSocket service
          }
        },
      });
  }

  private processIncomingMessage(data: any): void {
    
    if (!this.isValidDataMessage(data)) {
      return;
    }
    
    const event = this.parseEventData(data.event);
    if (!event) {
      return;
    }
    
    if (this.isDestroyed) return;
    this.handleEventMessage(event);
  }

  private isValidDataMessage(data: any): boolean {
    if (data?.type !== "data" || !data.event) {
      return false;
    }
    return true;
  }

  private parseEventData(eventData: any): any | null {
    try {
      const event = typeof eventData === 'string' 
        ? JSON.parse(eventData) 
        : eventData;
      
      if (!event || typeof event !== 'object' || !event.type) {
        return null;
      }
      
      return event;
    } catch (error) {
      return null;
    }
  }

  private generateSessionId(): string {
    return uuidv4();
  }

  public startNewSession(sessionId: string = ""): void {
    if (!sessionId) {
      sessionId = this.generateSessionId();
    }
    
    // Clear existing messages and state
    this.isTypingState.set(false);
    this.isThinkingState.set(false);
    this.messagesState.set([]);
    this.pendingProcessMessagesState.set([]);
    this.lastMessageTimestamp = null;
    
    // Set new session ID
    this.sessionId.set(sessionId);
    
    // Reconnect with new session
    const currentBotCode = this.botCode();
    if (currentBotCode) {
      this.webSocketEventService.disconnect();
      // Wait a bit for disconnection to complete before reconnecting
      setTimeout(() => {
        this.initializeWebSocketConnection(currentBotCode);
      }, 100);
    }
  }

  public switchBot(botCode: string, endpointToSend: string): void {
    
    // Only disconnect if we're switching to a different bot
    const isNewBot = this.botCode() !== botCode;
    
    if (isNewBot) {
      this.webSocketEventService.disconnect();
      this.messagesState.set([]);
      this.lastMessageTimestamp = null;
      this.sessionId.set(this.generateSessionId());
    }
    
    this.isTypingState.set(false);
    this.botCodeState.set(botCode);
    
    // Only reconnect if we disconnected or if there's no connection
    if (isNewBot || this.connection() === "DISCONNECTED") {
      this.initializeWebSocketConnection(botCode);
    }
  }



  private addMessage(
    id: string,
    content: string,
    isUser: boolean,
    customData: Partial<Omit<Message, "id" | "content" | "isUser" | "timestamp">> = {},
  ): void {
    const currentTime = new Date();
    const newMessage: Message = {
      id,
      content,
      isUser,
      timestamp: currentTime,
      ...customData,
    };
    this.messagesState.update((messages) => [...messages, newMessage]);
    this.lastMessageTimestamp = currentTime;
  }

  /**
   * Handles incoming event messages from AppSync Event API
   */
  private handleEventMessage(event: any): void {

    // Handle different event types based on Event API schema
    switch (event.type) {
      case "assistantMessage":
        this.handleAssistantMessage(event);
        break;
      case "articlesRetrieval":
        this.handleArticlesRetrieval(event);
        break;
      case "trace":
        this.handleTrace(event);
        break;
      default:
    }
  }

  /**
   * Handles assistantMessage events
   * Shows assistant responses in the chat
   */
  private handleAssistantMessage(event: any): void {
    this.isTypingState.set(false);

    const body = event.body || {};
    
    // Handle merged messages (simulación de escritura secuencial)
    if (body.mergedMessages?.length) {
      body.mergedMessages.forEach((msg: any) => {
        const { content, buttons, selectList } = parseMessageContent(msg.text);
        this.addMessage(msg.messageId, content, false, {
          messageType: 'assistant',
          buttons,
          selectList,
          agentName: event.agentName,
        });
      });
    }

    // Handle main message
    if (body.text) {
      const { content, buttons, selectList } = parseMessageContent(body.text);

      let retrievedArticles: ArticleNotification[] = [];
      if (body.answeredMessageId) {
        const articles = this.retrievedArticles[body.answeredMessageId];
        if (articles && Array.isArray(articles)) {
          retrievedArticles = articles;
        }
      }

      this.addMessage(event.messageId, content, false, {
        messageType: 'assistant',
        buttons,
        selectList,
        agentName: event.agentName,
        answeredMessageId: body.answeredMessageId,
        transferIntent: body.transferIntent,
        requestKey: body.requestKey,
        retrievedArticles: retrievedArticles
      });
    }

    // Only deactivate thinking state (keep process messages visible)
    this.isThinkingState.set(false);
  }

  /**
   * Handles articlesRetrieval events
   * Accumulates articles to show in collapsible section
   */
  private handleArticlesRetrieval(event: any): void {
    const body = event.body || {};
    const messageId = event.messageId;
    // Accumulate articles as process message
    if (body.articles?.length) {
      if (!messageId) return;
      
      // Inicializar array si no existe
      if (!this.retrievedArticles[messageId]) {
        this.retrievedArticles[messageId] = [];
      }
      
      // Agregar la nueva notificación al array
      this.retrievedArticles[messageId].push(body);
      
      // Activate thinking state when process messages start accumulating
      this.isThinkingState.set(true);
    }
  }

  /**
   * Handles trace events for debugging and transparency
   * Types: thinking, tool_use, tool_output, final_answer, model_invocation_info
   */
  private handleTrace(event: any): void {
    const traceBody = event.body?.body || {};
    const traceType = event.body?.type;
    const text = traceBody.text || '';

    // Any trace containing tool_call XML → summary


    switch (traceType) {
      case "thinking":
        this.addProcessTrace(event, 'thinking', text);
        this.isThinkingState.set(true);
        break;
      case "tool_use": {
        const paramsFormatted = this.formatToolParameters(traceBody.params);
        this.addProcessTrace(event, 'tool_use', `Herramienta: ${traceBody.tool_name}\n\nParámetros:\n${paramsFormatted}`);
        this.isTypingState.set(true);
        break;
      }
      case "tool_output":
        this.addProcessTrace(event, 'tool_output', text);
        break;
      case "final_answer":
        this.addProcessTrace(event, 'final_answer', text);
        this.isTypingState.set(true);
        break;
      case "model_invocation_info":
        this.addProcessTrace(event, 'model_invocation_info', `Invocación del modelo\n\n **Modelo:** ${traceBody.model_id || 'N/A'}\n`);
        break;

    }
  }

  private addProcessTrace(event: any, traceType: TraceType, content: string): void {
    this.pendingProcessMessagesState.update(messages => [
      ...messages,
      {
        id: `trace-${traceType}-${event.messageId}`,
        type: 'trace',
        traceType,
        content,
        timestamp: new Date(),
        answeredMessageId: event.messageId,
        agentName: event.agentName,
        data: event.body?.body || {},
      }
    ]);
  }



  //  Formats tool_used parameters for display in trace messages
  private formatToolParameters(
    params: Array<{ name: string; value: any }>,
    includeNames?: string[]
  ): string {
    if (!params || !Array.isArray(params)) return '';

    // Filter by include names if specified
    const filtered = includeNames && includeNames.length > 0
      ? params.filter(p => includeNames.includes(p.name))
      : params;

    return filtered
      .map(param => `${param.name}: ${param.value}`)
      .join('\n');
  }


  public sendMessage(content: string, whatsappNumber?: string): void {
    if (!content.trim() || this.isDestroyed) return;
    
    // Reset thinking state for new message
    this.isThinkingState.set(false);
    
    const tempId = "temp-" + uuidv4();
    this.addMessage(tempId, content, true, { messageType: 'user', sendingStatus: 'sending' });
    this.isTypingState.set(true);

    this.sendUserMessageToApi(
      content,
      this.sessionId(),
      whatsappNumber || "",
      tempId,
    )
      .then((success) => {
        if (!success && !this.isDestroyed) {
          this.isTypingState.set(false);
        }
      })
      .catch((error) => {
        if (!this.isDestroyed) {
          this.isTypingState.set(false);
        }
      });
  }

  /**
   * Sends user message to AppSync Event API HTTP endpoint
   * Same payload structure as before, only URL and API key source changes
   */
  private sendUserMessageToApi(
    userText: string,
    sessionId: string,
    whatsappNumber: string,
    tempId: string,
  ): Promise<boolean> {
    // Mock mode: simulate assistant response
    if (environment.mockAuth) {
      return this.sendMockAssistantResponse(userText, tempId);
    }

    // Same payload structure as original service
    const payload = {
      botId: this.botCode(),
      inputMessage: { type: "Text", text: userText },
      languageCode: "es-us",
      botSessionId: sessionId,
      genesysConversationId: sessionId,
      parameters: { WhatsappNumber: whatsappNumber },
      sessionSource: "customFront",
    };

    // Get JWT token from OAuthService
    const token = this.oauthService?.getAccessToken();
    
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    });

    // Construct the endpoint URL using baseApiUrl
    const httpEndpoint = `${environment.baseApiUrl}/chatbot/send-message`;

    return new Promise<boolean>((resolve) => {
      this.http
        .post(httpEndpoint, payload, { headers })
        .subscribe({
          next: (response: any) => {
            // Update temporary message ID with real one from response
            const newId = response.responseData?.parameters?.message_id || tempId;
            this.messagesState.update((messages) =>
              messages.map((msg) =>
                msg.id === tempId ? { ...msg, id: newId, sendingStatus: 'sent' } : msg
              )
            );

            resolve(true);
          },
          error: (error) => {
            // Update message status to error
            this.messagesState.update((messages) =>
              messages.map((msg) =>
                msg.id === tempId ? { ...msg, sendingStatus: 'error' } : msg
              )
            );
            
            this.addMessage(
              "error",
              `❌ <b>Error al enviar mensaje:</b><br>${error.message || "Error desconocido"}`,
              false,
            );
            this.isTypingState.set(false);
            resolve(false);
          },
        });
    });
  }

  /**
   * Simulates assistant response for mock mode
   */
  private sendMockAssistantResponse(userText: string, tempId: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      // Update user message ID and status to sent
      const userMessageId = `user-${uuidv4()}`;
      this.messagesState.update((messages) =>
        messages.map((msg) =>
          msg.id === tempId ? { ...msg, id: userMessageId, sendingStatus: 'sent' } : msg
        )
      );

      // Simulate trace: thinking
      setTimeout(() => {
        if (this.isDestroyed) {
          resolve(false);
          return;
        }

        this.isThinkingState.set(true);
        this.pendingProcessMessagesState.update(messages => [
          ...messages,
          {
            id: `trace-thinking-${uuidv4()}`,
            type: 'trace',
            traceType: 'thinking',
            content: `💭 Analizando la pregunta del usuario: "${userText}"`,
            timestamp: new Date(),
            answeredMessageId: userMessageId,
            agentName: 'Mock Assistant',
            data: { text: `Analizando: "${userText}"` },
          }
        ]);
      }, 300);

      // Simulate trace: tool_use
      setTimeout(() => {
        if (this.isDestroyed) {
          resolve(false);
          return;
        }

        this.pendingProcessMessagesState.update(messages => [
          ...messages,
          {
            id: `trace-tool-use-${uuidv4()}`,
            type: 'trace',
            traceType: 'tool_use',
            content: `🔧 Usando herramienta: knowledge_base_search\n\nParámetros:\n\`\`\`json\n{\n  "query": "${userText}",\n  "max_results": 5\n}\n\`\`\``,
            timestamp: new Date(),
            answeredMessageId: userMessageId,
            agentName: 'Mock Assistant',
            data: { tool_name: 'knowledge_base_search', params: { query: userText, max_results: 5 } },
          }
        ]);
      }, 800);

      // Simulate trace: tool_output
      setTimeout(() => {
        if (this.isDestroyed) {
          resolve(false);
          return;
        }

        this.pendingProcessMessagesState.update(messages => [
          ...messages,
          {
            id: `trace-tool-output-${uuidv4()}`,
            type: 'trace',
            traceType: 'tool_output',
            content: `✅ Resultado: Se encontraron 3 documentos relevantes con información relacionada.`,
            timestamp: new Date(),
            answeredMessageId: userMessageId,
            agentName: 'Mock Assistant',
            data: { text: 'Se encontraron 3 documentos relevantes' },
          }
        ]);
      }, 1200);

      // Simulate final response
      setTimeout(() => {
        if (this.isDestroyed) {
          resolve(false);
          return;
        }

        // Generate mock response based on user input
        const mockResponse = this.generateMockResponse(userText);
        const assistantMessageId = `assistant-${uuidv4()}`;

        // Add assistant message
        this.addMessage(assistantMessageId, mockResponse, false, {
          messageType: 'assistant',
          agentName: 'Mock Assistant',
          answeredMessageId: userMessageId,
        });

        this.isTypingState.set(false);
        this.isThinkingState.set(false);
        resolve(true);
      }, 2000); // 2 second total delay to simulate processing
    });
  }

  /**
   * Generates contextual mock responses
   */
  private generateMockResponse(userText: string): string {
    const lowerText = userText.toLowerCase();

    // Contextual responses based on keywords
    if (lowerText.includes('hola') || lowerText.includes('hello') || lowerText.includes('hi')) {
      return '¡Hola! 👋 Soy un asistente de prueba. ¿En qué puedo ayudarte hoy?';
    }
    
    if (lowerText.includes('ayuda') || lowerText.includes('help')) {
      return 'Por supuesto, estoy aquí para ayudarte. Este es un entorno de prueba con respuestas simuladas. ¿Tienes alguna pregunta específica?';
    }
    
    if (lowerText.includes('precio') || lowerText.includes('costo') || lowerText.includes('price')) {
      return 'Los precios varían según el producto o servicio. ¿Podrías especificar sobre qué producto necesitas información?';
    }
    
    if (lowerText.includes('horario') || lowerText.includes('hora') || lowerText.includes('schedule')) {
      return 'Nuestro horario de atención es de lunes a viernes de 9:00 AM a 6:00 PM. ¿Necesitas algo más?';
    }
    
    if (lowerText.includes('gracias') || lowerText.includes('thank')) {
      return '¡De nada! 😊 Si necesitas algo más, no dudes en preguntar.';
    }
    
    if (lowerText.includes('adiós') || lowerText.includes('chao') || lowerText.includes('bye')) {
      return '¡Hasta luego! Que tengas un excelente día. 👋';
    }
    
    // Default response
    const responses = [
      `Entiendo tu consulta sobre "${userText}". Déjame buscar esa información para ti.`,
      `Gracias por tu mensaje. Como este es un entorno de prueba, estoy simulando una respuesta. ¿Puedo ayudarte con algo más?`,
      `He recibido tu mensaje: "${userText}". En producción, aquí recibirías una respuesta del sistema real.`,
      `Interesante pregunta. En modo de prueba puedo ayudarte con consultas básicas. ¿Qué más necesitas saber?`,
    ];
    
    return responses[getSecureRandomIndex(responses.length)];
  }

  public clearMessages(): void {
    if (this.isDestroyed) return;
    
    this.isTypingState.set(false);
    this.isThinkingState.set(false);
    this.messagesState.set([]);
    this.pendingProcessMessagesState.set([]);
    this.lastMessageTimestamp = null;
  }

  public ngOnDestroy(): void {
    if (this.isDestroyed) {
      return;
    }
    
    this.isTypingState.set(false);
    this.isDestroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    this.webSocketEventService.disconnect();
  }
}
